export const BASE_STRATEGY_ALLOCATION_LIMIT = 100;
// Retirement accounts reserve a minimum cash buffer for trade settlement, so
// their maximum strategy allocation is capped below the standard 100%.
export const RETIREMENT_STRATEGY_ALLOCATION_LIMIT = 95;

const MARGIN_ENABLED_KEY = "margin_enabled";
const MARGIN_ALLOWED_AMOUNT_KEY = "margin_allowed_amt";
const RETIREMENT_KEY = "isretirement";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseBooleanSetting(value: unknown): boolean {
  if (value === true) return true;
  if (typeof value !== "string") return false;
  return value.trim().toLowerCase() === "true";
}

function parsePositiveIntegerSetting(value: unknown): number {
  if (typeof value === "number") {
    return Number.isInteger(value) && value > 0 ? value : 0;
  }

  if (typeof value !== "string") return 0;
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return 0;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 0;
}

export function getStrategyAllocationLimit(
  userSettings: unknown,
  baseLimit: number = BASE_STRATEGY_ALLOCATION_LIMIT
): number {
  if (!isRecord(userSettings)) return baseLimit;
  if (!parseBooleanSetting(userSettings[MARGIN_ENABLED_KEY])) {
    return baseLimit;
  }

  return baseLimit + parsePositiveIntegerSetting(userSettings[MARGIN_ALLOWED_AMOUNT_KEY]);
}

export function getStrategyAllocationLimitForAccount(
  configData: unknown,
  isRetirement: boolean
): number {
  const baseLimit = isRetirement
    ? RETIREMENT_STRATEGY_ALLOCATION_LIMIT
    : BASE_STRATEGY_ALLOCATION_LIMIT;
  if (!isRecord(configData)) return baseLimit;
  const userSettings = configData.userSettings;
  if (
    isRecord(userSettings) &&
    (MARGIN_ENABLED_KEY in userSettings || MARGIN_ALLOWED_AMOUNT_KEY in userSettings)
  ) {
    return getStrategyAllocationLimit(userSettings, baseLimit);
  }
  return getStrategyAllocationLimit(configData, baseLimit);
}

export function getStrategyAllocationLimitFromConfigData(configData: unknown): number {
  return getStrategyAllocationLimitForAccount(
    configData,
    isRecord(configData) && parseBooleanSetting(configData[RETIREMENT_KEY])
  );
}

// The weight to seed a fresh Plutus Managed allocation at. It must respect the
// account's base cap (retirement reserves a settlement buffer, so 95% not 100%)
// and deliberately ignores margin: auto-provisioning should fill the account,
// never lever it.
export function getManagedStrategySeedWeight(isRetirement: boolean): number {
  return isRetirement
    ? RETIREMENT_STRATEGY_ALLOCATION_LIMIT
    : BASE_STRATEGY_ALLOCATION_LIMIT;
}

export function getManagedStrategySeedWeightFromConfigData(configData: unknown): number {
  return getManagedStrategySeedWeight(
    isRecord(configData) && parseBooleanSetting(configData[RETIREMENT_KEY])
  );
}
